import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';

// API configuration
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Verify API URL is correct (debug)
if (typeof window !== 'undefined' && API_URL.includes('apl')) {
  console.error('❌ ERROR: API_URL contains typo "apl" instead of "api"!', API_URL);
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Log API URL in development
if (process.env.NODE_ENV === 'development') {
  console.log('🔗 API Base URL:', API_URL);
}

// Request interceptor - Add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log full URL for debugging (check for typos)
    const fullUrl = `${config.baseURL}${config.url}`;
    if (fullUrl.includes('/apl/')) {
      console.error('❌ TYPO DETECTED: URL contains /apl/ instead of /api/', fullUrl);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors
apiClient.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error: AxiosError) => {
    if (error.response) {
      // Server responded with error
      const { status, data } = error.response;

      if (status === 401) {
        // Unauthorized - clear auth data and redirect to login
        localStorage.removeItem('auth_token');
        localStorage.removeItem('admin_token');
        localStorage.removeItem('current_admin');
        localStorage.removeItem('user_data');
        window.location.href = '/';
      }

      const apiError = new Error((data as any)?.message || 'Request failed');
      (apiError as any).data = data;
      (apiError as any).status = status;
      throw apiError;
    } else if (error.request) {
      // Request made but no response
      throw new Error('Network error. Please check your connection.');
    } else {
      // Something else happened
      throw new Error(error.message || 'An unexpected error occurred');
    }
  }
);

// Helper functions for different HTTP methods
export const apiGet = <T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> => {
  return apiClient.get(url, config);
};

export const apiPost = <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> => {
  return apiClient.post(url, data, config);
};

export const apiPut = <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> => {
  return apiClient.put(url, data, config);
};

export const apiPatch = <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> => {
  return apiClient.patch(url, data, config);
};

export const apiDelete = <T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> => {
  return apiClient.delete(url, config);
};

export default apiClient;

